import { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList, ReferenceArea } from 'recharts'
import { Button } from '@/common/ui/Form/Button/Button'
import { Card } from '@/common/ui/Card/Card'
import { cn } from '@/common/lib/cn/cn'
import type { BrandComparisonBrandData } from '../../model/useCompanyStatsBrandComparison'
import { BRAND_COMPARE_COLORS, mergeScoreDistributions } from '../../lib/brandComparison'
import { CompanyStatsBrandComparisonLegendDot } from './CompanyStatsBrandComparisonHeader'
import {
    handleChartMouseDown,
    nonInteractiveBarChartStyle,
    nonInteractiveBarProps,
    nonInteractiveChartClassName,
} from '@/common/ui/ScoreDistributionChart/nonInteractiveChart'

type CompanyStatsBrandComparisonScoreChartProps = {
    brandA: BrandComparisonBrandData
    brandB: BrandComparisonBrandData
}

const SCORE_ZONE_LEGEND = [
    { range: '1–5', color: '#FF484B' },
    { range: '6–7', color: '#F59E0B' },
    { range: '8–10', color: '#08AC81' },
] as const

const SCORE_ZONES = [
    {
        x1: '1',
        x2: '5',
        fill: '#FF484B',
        fillOpacity: 0.14,
        label: 'СЛАБО',
        labelColor: '#FF484B',
    },
    {
        x1: '6',
        x2: '7',
        fill: '#F59E0B',
        fillOpacity: 0.14,
        label: 'СРЕДНЕ',
        labelColor: '#F59E0B',
    },
    {
        x1: '8',
        x2: '10',
        fill: '#08AC81',
        fillOpacity: 0.14,
        label: 'ХОРОШО',
        labelColor: '#08AC81',
    },
] as const

const renderCountLabel = (value: unknown) => {
    const count = Number(value)
    if (!count) return ''
    return String(count)
}

export const CompanyStatsBrandComparisonScoreChart = ({
    brandA,
    brandB,
}: CompanyStatsBrandComparisonScoreChartProps) => {
    const chartData = useMemo(
        () => mergeScoreDistributions(brandA.common.scores, brandB.common.scores),
        [brandA.common.scores, brandB.common.scores]
    )

    const maxCount = useMemo(
        () => Math.max(...chartData.map(d => Math.max(d.brandACount, d.brandBCount)), 1),
        [chartData]
    )

    return (
        <Card color='primary' className='h-full'>
            <div className='flex h-full min-h-[420px] flex-col gap-4'>
                <div className='flex items-start justify-between gap-4'>
                    <h3 className='text-xs font-bold uppercase tracking-wide text-white'>Распределение баллов</h3>
                    <Button type='button' variant='ghost' size='sm' disabled aria-label='Скачать график в PNG'>
                        PNG
                    </Button>
                </div>

                <div className='flex flex-wrap items-center gap-3 text-sm text-base-content/70'>
                    <span className='inline-flex items-center gap-2'>
                        <CompanyStatsBrandComparisonLegendDot side='brandA' />
                        {brandA.name}
                    </span>
                    <span className='text-base-content/40' aria-hidden>
                        vs
                    </span>
                    <span className='inline-flex items-center gap-2'>
                        <CompanyStatsBrandComparisonLegendDot side='brandB' />
                        {brandB.name}
                    </span>
                </div>

                <div
                    className={cn('flex flex-1 items-center justify-center', nonInteractiveChartClassName)}
                    onMouseDown={handleChartMouseDown}
                    role='img'
                    aria-hidden
                >
                    <div className='h-full w-full min-h-[300px] max-h-[360px]'>
                        <ResponsiveContainer width='100%' height='100%'>
                            <BarChart
                                data={chartData}
                                margin={{ top: 36, right: 12, left: 0, bottom: 8 }}
                                barGap={2}
                                style={nonInteractiveBarChartStyle}
                            >
                                <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.08)' vertical={false} />
                                <XAxis
                                    dataKey='label'
                                    stroke='#a6adbb'
                                    tick={{ fill: '#a6adbb', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke='#a6adbb'
                                    tick={{ fill: '#a6adbb', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                    domain={[0, maxCount]}
                                    allowDecimals={false}
                                />
                                {SCORE_ZONES.map(zone => (
                                    <ReferenceArea
                                        key={zone.label}
                                        x1={zone.x1}
                                        x2={zone.x2}
                                        y1={0}
                                        y2={maxCount}
                                        fill={zone.fill}
                                        fillOpacity={zone.fillOpacity}
                                        strokeOpacity={0}
                                        ifOverflow='visible'
                                        label={{
                                            value: zone.label,
                                            position: 'insideTop',
                                            fill: zone.labelColor,
                                            fontSize: 10,
                                            fontWeight: 700,
                                        }}
                                    />
                                ))}
                                <Bar
                                    dataKey='brandACount'
                                    fill={BRAND_COMPARE_COLORS.brandA}
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={24}
                                    name={brandA.name}
                                    {...nonInteractiveBarProps}
                                >
                                    <LabelList
                                        dataKey='brandACount'
                                        position='top'
                                        fill='#e5e7eb'
                                        fontSize={10}
                                        formatter={renderCountLabel}
                                    />
                                </Bar>
                                <Bar
                                    dataKey='brandBCount'
                                    fill={BRAND_COMPARE_COLORS.brandB}
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={24}
                                    name={brandB.name}
                                    {...nonInteractiveBarProps}
                                >
                                    <LabelList
                                        dataKey='brandBCount'
                                        position='top'
                                        fill='#e5e7eb'
                                        fontSize={10}
                                        formatter={renderCountLabel}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className='flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3 text-xs text-base-content/70'>
                    <div className='flex flex-wrap items-center gap-4'>
                        <span className='inline-flex items-center gap-2'>
                            <span
                                className='inline-block h-2.5 w-2.5 rounded-sm'
                                style={{ backgroundColor: BRAND_COMPARE_COLORS.brandA }}
                                aria-hidden
                            />
                            {brandA.name}
                        </span>
                        <span className='inline-flex items-center gap-2'>
                            <span
                                className='inline-block h-2.5 w-2.5 rounded-sm'
                                style={{ backgroundColor: BRAND_COMPARE_COLORS.brandB }}
                                aria-hidden
                            />
                            {brandB.name}
                        </span>
                    </div>
                    <div className='flex flex-wrap items-center gap-3'>
                        {SCORE_ZONE_LEGEND.map(item => (
                            <span key={item.range} className='inline-flex items-center gap-1.5'>
                                <span
                                    className='inline-block h-0.5 w-4 rounded-full'
                                    style={{ backgroundColor: item.color }}
                                    aria-hidden
                                />
                                {item.range}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </Card>
    )
}
